/**
 * The request-credits dialog (frame `67:144`), opened over Créditos by its card's own button, or
 * from another screen through `/creditos?pedir=creditos`: the page owns `open` through that search
 * param. `tenantId` is a prop: the page mounts this inline.
 *
 * A new request shows only what the reader needs to ask: the balance available now and the date
 * it covers collection until, the amount (three presets, or "Outro" with a number of their own),
 * and an optional reason. Earlier requests and their answers live in "Ver todos os pedidos"
 * (`OpenRequests.tsx`), never above a new one.
 *
 * The API exposes DELETE and no PATCH on a credit request (§18.6): a tenant may hold at most one
 * OPEN or IN_REVIEW request at a time, and changing one already raised is withdraw-and-resend. So
 * the create form and the open-request view are mutually exclusive, and withdrawing sits behind
 * its own confirm dialog.
 */

import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';

import { getLocale, type Locale } from '../../../i18n';
import { credits as formatCredits, integer } from '../../../i18n/format';
import { ConfirmDialog, DialogSurfaceFooter, FormDialog } from '../../../chrome/form';
import { ft } from '../../../chrome/form/messages';
import { DefinitionList, type Definition } from '../../../chrome/page';
import { Button } from '../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../ui/dialog';
import { Field, FieldError, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { SEGMENTED_ITEM_CLASSNAME, ToggleGroup, ToggleGroupItem } from '../../../ui/toggle-group';
import { tt, type TenantMessageKey } from '../i18n';
import { rm } from '../request/messages';
import { useCredits, useFundedWindow } from '../balance';
import {
  openRequestOf,
  useCapacityRequests,
  useCreateCapacityRequest,
  useWithdrawCapacityRequest,
} from './api/queries';
import type { CapacityRequest } from './api/schemas';
import type { FundedWindow } from '../api/schemas';

const STATE_LABEL_KEY: Record<CapacityRequest['state'], TenantMessageKey> = {
  OPEN: 'request.state.OPEN',
  IN_REVIEW: 'request.state.IN_REVIEW',
  GRANTED: 'request.state.GRANTED',
  DECLINED: 'request.state.DECLINED',
  WITHDRAWN: 'request.state.WITHDRAWN',
};

/** Frame `67:144`'s three presets. "Outro" is the fourth segment: `credits` is `null` while it is
 *  picked, and the amount comes from its own field. */
const CREDIT_PRESETS = [40_000, 80_000, 120_000] as const;
const DEFAULT_CREDITS: number = CREDIT_PRESETS[1];

function creditsToValue(credits: number | null): string {
  return credits === null ? 'other' : String(credits);
}

function valueToCredits(value: string): number | null {
  return value === 'other' ? null : Number(value);
}

function formatDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale).format(new Date(iso));
}

/** A whole number of credits above zero, or `null` for anything else. */
function otherAmount(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value > 0 ? value : null;
}

function formatThousands(value: number, locale: Locale): string {
  return rm('quanto.option', {
    amount: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value / 1000),
  });
}

/** `credits_per_hour` is null while some tracked operation is unpriced
 *  (§18.1) -- no figure renders rather than one that happens to be wrong. */
function dailyRate(fundedWindow: FundedWindow): number | null {
  return fundedWindow.credits_per_hour === null ? null : fundedWindow.credits_per_hour * 24;
}

interface Draft {
  credits: number | null;
  other: string;
  context: string;
}

const EMPTY_DRAFT: Draft = { credits: DEFAULT_CREDITS, other: '', context: '' };

/** One draft per tenant, so two tenants open in the same browser do not
 *  overwrite each other's. This is what survives a session dropping mid-form
 *  (`SignInRequired`'s redirect round trip) -- without it, signing back in
 *  gets someone back to this page with everything they typed gone. */
function draftKey(tenantId: string): string {
  return `credit-request-draft:${tenantId}`;
}

function readDraft(tenantId: string): Draft {
  try {
    const raw = localStorage.getItem(draftKey(tenantId));
    return raw === null ? EMPTY_DRAFT : { ...EMPTY_DRAFT, ...(JSON.parse(raw) as Partial<Draft>) };
  } catch {
    return EMPTY_DRAFT;
  }
}

function clearDraft(tenantId: string): void {
  try {
    localStorage.removeItem(draftKey(tenantId));
  } catch {
    // Best-effort: a blocked store never held the draft in the first place.
  }
}

/** The open request's own state and when it was raised: the confirmation a tenant reads while
 *  it waits. */
function OpenRequestSummary(props: { request: CapacityRequest; locale: string }): ReactNode {
  const { request, locale } = props;
  return (
    <div className="flex flex-col gap-1">
      <p data-testid="request-state">{tt(STATE_LABEL_KEY[request.state])}</p>
      <p data-testid="request-created">{formatDate(request.created_at, locale)}</p>
    </div>
  );
}

/** The balance available now, what the organisation spends a day, and the date the balance
 *  covers collection until, each only once its own query has answered. */
function BalanceContext(props: { tenantId: string; locale: Locale }): ReactNode {
  const { data: credits } = useCredits(props.tenantId);
  const { data: fundedWindow } = useFundedWindow(props.tenantId);
  const items: Definition[] = [];
  if (credits !== undefined) {
    items.push({
      term: rm('context.balance'),
      detail: formatCredits(credits.available, props.locale),
    });
  }
  const rate = fundedWindow === undefined ? null : dailyRate(fundedWindow);
  if (rate !== null) {
    items.push({ term: rm('context.rate'), detail: formatThousands(rate, props.locale) });
  }
  if (fundedWindow?.funded_until != null) {
    items.push({
      term: rm('context.runway'),
      detail: formatDate(fundedWindow.funded_until, props.locale),
    });
  }
  return items.length > 0 ? <DefinitionList items={items} columns={3} /> : null;
}

export function RequestCreditsDialog(props: {
  tenantId: string;
  trigger: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}): ReactNode {
  const { tenantId, trigger } = props;
  const locale = getLocale();
  const quantoLabelId = useId();
  const otherId = useId();
  const otherErrorId = useId();
  const contextId = useId();

  const [ownOpen, setOwnOpen] = useState(props.defaultOpen ?? false);
  const open = props.open ?? ownOpen;
  const setOpen = props.onOpenChange ?? setOwnOpen;
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const requestsQuery = useCapacityRequests(tenantId);
  const createRequest = useCreateCapacityRequest(tenantId);
  const withdrawRequest = useWithdrawCapacityRequest(tenantId);

  const [credits, setCredits] = useState(() => readDraft(tenantId).credits);
  const [other, setOther] = useState(() => readDraft(tenantId).other);
  const [context, setContext] = useState(() => readDraft(tenantId).context);
  const [otherError, setOtherError] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(draftKey(tenantId), JSON.stringify({ credits, other, context }));
    } catch {
      // Best-effort: a full or disabled store costs the draft, not the submit.
    }
  }, [tenantId, credits, other, context]);

  // The list holds both kinds; an open SLOTS request is not a pending credit request.
  const requests = (requestsQuery.data ?? []).filter((request) => request.kind === 'CREDITS');
  const openRequest = openRequestOf(requests, 'CREDITS');

  function handleSubmit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    const amount = credits ?? otherAmount(other);
    if (amount === null) {
      setOtherError(rm('quanto.other.invalid'));
      return;
    }
    setOtherError(null);
    return createRequest
      .mutateAsync({
        kind: 'CREDITS',
        accounts_to_track: null,
        credits_requested: amount,
        slots_requested: null,
        context: context.trim(),
      })
      .then(() => clearDraft(tenantId));
  }

  if (openRequest !== undefined) {
    return (
      <>
        {/* Not modal (§18.6): a tenant sent here from another screen must
         * still be able to use the shell around it -- sign out, most of all. `scrim` ships the frame's own dimming without
         * Radix's modal overlay (which renders nothing once `modal` is
         * false); `onPointerDownOutside` keeps a click on it from dismissing
         * the dialog the way an un-overridden non-modal `Dialog` otherwise
         * would. */}
        <Dialog open={open} onOpenChange={setOpen} modal={false}>
          <DialogTrigger asChild>{trigger}</DialogTrigger>
          <DialogContent scrim onPointerDownOutside={(event) => event.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{tt('request.title')}</DialogTitle>
              <DialogDescription>{tt('request.lead')}</DialogDescription>
            </DialogHeader>
            <OpenRequestSummary request={openRequest} locale={locale} />
            <p className="text-body-sm text-muted-foreground">
              {tt('request.open_exists', { date: formatDate(openRequest.created_at, locale) })}
            </p>
            <DialogSurfaceFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {ft('close')}
              </Button>
              <Button type="button" onClick={() => setWithdrawOpen(true)}>
                {tt('request.withdraw')}
              </Button>
            </DialogSurfaceFooter>
          </DialogContent>
        </Dialog>
        <ConfirmDialog
          open={withdrawOpen}
          onOpenChange={setWithdrawOpen}
          title={rm('withdraw.title')}
          description={rm('withdraw.body')}
          confirmLabel={rm('withdraw.confirm')}
          pending={withdrawRequest.isPending}
          failure={withdrawRequest.error?.failure ?? null}
          destructive
          onConfirm={() => withdrawRequest.mutateAsync({ requestId: openRequest.id })}
        />
      </>
    );
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      title={tt('request.title')}
      submitLabel={tt('request.submit')}
      pending={createRequest.isPending}
      failure={createRequest.error?.failure ?? null}
      onSubmit={handleSubmit}
      // Same reason as the open-request `Dialog` above: the shell around it
      // stays usable. This IS frame `67:144`, so it ships the frame's dimming too.
      modal={false}
      scrim
    >
      <BalanceContext tenantId={tenantId} locale={locale} />

      <Field>
        <FieldLabel id={quantoLabelId}>{rm('quanto.label')}</FieldLabel>
        <ToggleGroup
          type="single"
          aria-labelledby={quantoLabelId}
          value={creditsToValue(credits)}
          onValueChange={(value) => {
            if (value !== '') setCredits(valueToCredits(value));
          }}
          className="bg-muted w-full rounded-md p-1"
        >
          {CREDIT_PRESETS.map((preset) => (
            <ToggleGroupItem
              key={preset}
              value={String(preset)}
              className={`${SEGMENTED_ITEM_CLASSNAME} flex-1`}
            >
              {rm('quanto.option', { amount: integer(preset / 1000, locale) })}
            </ToggleGroupItem>
          ))}
          <ToggleGroupItem value="other" className={`${SEGMENTED_ITEM_CLASSNAME} flex-1`}>
            {rm('quanto.other')}
          </ToggleGroupItem>
        </ToggleGroup>
      </Field>

      {credits === null && (
        <Field data-invalid={otherError !== null || undefined}>
          <FieldLabel htmlFor={otherId}>{rm('quanto.other.label')}</FieldLabel>
          <Input
            id={otherId}
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={other}
            onChange={(event) => setOther(event.target.value)}
            aria-invalid={otherError !== null || undefined}
            aria-describedby={otherError !== null ? otherErrorId : undefined}
          />
          <FieldError id={otherErrorId}>{otherError}</FieldError>
        </Field>
      )}

      <Field>
        <FieldLabel htmlFor={contextId}>{rm('context.label')}</FieldLabel>
        <Textarea
          id={contextId}
          value={context}
          onChange={(event) => setContext(event.target.value)}
        />
      </Field>
    </FormDialog>
  );
}
